import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Plus, Trash2, Users, Loader2, UserPlus } from 'lucide-react';

const DistributionListModal = ({ open, onOpenChange, list, onSave, teamMembers = [] }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [recipients, setRecipients] = useState([]);
  const [saving, setSaving] = useState(false);
  const recipientsContainerRef = useRef(null);
  const newRecipientRef = useRef(null);

  // Filter stakeholders that have email addresses
  const stakeholdersWithEmail = teamMembers.filter(tm => tm.email?.trim());

  useEffect(() => {
    if (list) {
      setName(list.name || '');
      setDescription(list.description || '');
      setRecipients(list.recipients || []);
    } else {
      setName('');
      setDescription('');
      setRecipients([]);
    }
  }, [list, open]);

  // Auto-focus and scroll when adding a new recipient
  useEffect(() => {
    if (newRecipientRef.current) {
      newRecipientRef.current.focus();
      newRecipientRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      newRecipientRef.current = null;
    }
  }, [recipients.length]);

  // Check if a stakeholder is already in the recipients list
  const isStakeholderSelected = (stakeholder) => {
    return recipients.some(r => 
      r.email?.toLowerCase() === stakeholder.email?.toLowerCase()
    );
  };

  // Toggle stakeholder selection
  const toggleStakeholder = (stakeholder) => {
    if (isStakeholderSelected(stakeholder)) {
      // Remove from recipients
      setRecipients(recipients.filter(r => 
        r.email?.toLowerCase() !== stakeholder.email?.toLowerCase()
      ));
    } else {
      // Add to recipients
      setRecipients([...recipients, {
        name: stakeholder.name,
        email: stakeholder.email,
        role: stakeholder.role || ''
      }]);
    }
  };

  const handleAddRecipient = () => {
    setRecipients([...recipients, { name: '', email: '', role: '' }]);
  };

  const handleRemoveRecipient = (index) => {
    setRecipients(recipients.filter((_, i) => i !== index));
  };

  const handleRecipientChange = (index, field, value) => {
    const updated = [...recipients];
    updated[index] = { ...updated[index], [field]: value };
    setRecipients(updated);
  };

  // Handle Enter key in Role field to add new recipient
  const handleRoleKeyDown = (e, index) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddRecipient();
    }
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    
    // Filter out recipients with empty name AND empty email
    const validRecipients = recipients.filter(r => r.name?.trim() || r.email?.trim());
    // Further filter to only keep those with valid email
    const finalRecipients = validRecipients.filter(r => r.email?.trim());
    
    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        description: description.trim() || null,
        recipients: finalRecipients
      });
    } finally {
      setSaving(false);
    }
  };

  // Get manual recipients (those not matching any stakeholder)
  const manualRecipients = recipients.filter(r => 
    !stakeholdersWithEmail.some(s => s.email?.toLowerCase() === r.email?.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{list ? 'Edit Distribution List' : 'Create Distribution List'}</DialogTitle>
          <DialogDescription>
            {list 
              ? 'Update the distribution list details and recipients.'
              : 'Create a new list of recipients for email distribution.'
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4 overflow-y-auto flex-1">
          {/* Basic Info */}
          <div className="grid gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">List Name *</Label>
              <Input
                id="name"
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Executive Team, Stakeholders"
                data-testid="list-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description for this list"
                rows={2}
              />
            </div>
          </div>

          {/* Key Stakeholders Selection */}
          {stakeholdersWithEmail.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-slate-500" />
                <Label>Select from Key Stakeholders</Label>
              </div>
              <p className="text-xs text-slate-500">Click to add stakeholders to this distribution list</p>
              
              <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                {stakeholdersWithEmail.map((stakeholder) => {
                  const isSelected = isStakeholderSelected(stakeholder);
                  return (
                    <label
                      key={stakeholder.id || stakeholder.email}
                      className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50 transition-colors ${
                        isSelected ? 'bg-blue-50' : ''
                      }`}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleStakeholder(stakeholder)}
                        data-testid={`stakeholder-checkbox-${stakeholder.email}`}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{stakeholder.name}</p>
                        <p className="text-xs text-slate-500 truncate">{stakeholder.email}</p>
                      </div>
                      {stakeholder.role && (
                        <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded">
                          {stakeholder.role}
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* Manual Recipients */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <UserPlus className="h-4 w-4 text-slate-500" />
                <Label>Add Others Manually</Label>
              </div>
            </div>
            <p className="text-xs text-slate-500">Add recipients who are not in Key Stakeholders</p>

            {manualRecipients.length === 0 ? (
              <div className="border border-dashed rounded-lg p-4 text-center">
                <p className="text-sm text-slate-500 mb-2">No additional recipients</p>
                <Button variant="outline" size="sm" onClick={handleAddRecipient}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Recipient
                </Button>
              </div>
            ) : (
              <div className="space-y-2" ref={recipientsContainerRef}>
                {/* Header */}
                <div className="grid grid-cols-[1fr_1.5fr_1fr_40px] gap-2 text-xs font-medium text-slate-500 px-1">
                  <span>Name</span>
                  <span>Email *</span>
                  <span>Role</span>
                  <span></span>
                </div>
                
                {/* Manual recipient rows */}
                {recipients.map((recipient, index) => {
                  // Skip stakeholder-sourced recipients in this section
                  const isFromStakeholder = stakeholdersWithEmail.some(
                    s => s.email?.toLowerCase() === recipient.email?.toLowerCase()
                  );
                  if (isFromStakeholder) return null;
                  
                  return (
                    <div key={index} className="grid grid-cols-[1fr_1.5fr_1fr_40px] gap-2 items-center">
                      <Input
                        ref={index === recipients.length - 1 && !recipient.name && !recipient.email ? newRecipientRef : null}
                        value={recipient.name}
                        onChange={(e) => handleRecipientChange(index, 'name', e.target.value)}
                        placeholder="Name"
                        className="h-9"
                        data-testid={`recipient-name-${index}`}
                      />
                      <Input
                        type="email"
                        value={recipient.email}
                        onChange={(e) => handleRecipientChange(index, 'email', e.target.value)}
                        placeholder="email@example.com"
                        className="h-9"
                        data-testid={`recipient-email-${index}`}
                      />
                      <Input
                        value={recipient.role || ''}
                        onChange={(e) => handleRecipientChange(index, 'role', e.target.value)}
                        onKeyDown={(e) => handleRoleKeyDown(e, index)}
                        placeholder="Role"
                        className="h-9"
                        data-testid={`recipient-role-${index}`}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 text-slate-400 hover:text-red-600"
                        onClick={() => handleRemoveRecipient(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  );
                })}

                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleAddRecipient}
                  className="w-full border border-dashed"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Another Recipient
                </Button>
              </div>
            )}
          </div>

          {/* Summary */}
          {recipients.length > 0 && (
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-sm font-medium text-slate-700">
                Total Recipients: {recipients.filter(r => r.email?.trim()).length}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={!name.trim() || saving}
            data-testid="save-list-btn"
          >
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {list ? 'Save Changes' : 'Create List'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DistributionListModal;
